import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { Store } from '@ngxs/store';
import { of, switchMap, forkJoin, Observable } from 'rxjs';
import { ParentsInformationService } from '../../../../services/parents-information/parents-information.service';
import { UserSelectors } from '../../../../states/user/user-selector';
import { ParentsRelation } from '../../../../models/enums/parentsRelation';
import { ParentsInformationRequest } from '../../../../models/parents-information/parentsInformationRequest';

@Component({
  selector: 'app-parent-details-card',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './parent-details-card.component.html',
  styleUrl: './parent-details-card.component.scss'
})
export class ParentDetailsCardComponent implements OnInit {

  loading = false;
  lastUpdatedText = '3 months ago';
  submitted = false;

  parentDetailsForm!: FormGroup;

  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private store = inject(Store);
  private parentsInfoService = inject(ParentsInformationService);

  private studentId: string | null = null;
  private fatherId?: number;
  private motherId?: number;

  ngOnInit(): void {
    this.parentDetailsForm = this.fb.group({
      fatherFullName: [''],
      fatherEmail:    ['', [ Validators.email]],
      fatherPhone:    ['', [this.mobileValidator]],
      motherFullName: [''],
      motherEmail:    ['', [ Validators.email]],
      motherPhone:    ['', [this.mobileValidator]]
    });
    this.setLastUpdatedText();

    this.store.select(UserSelectors.getUser).pipe(
      switchMap(user => {
        if (!user || !user.id) return of(null);
        this.studentId = user.id;
        return this.parentsInfoService.getParentsInformationByStudentId(user.id);
      })
    ).subscribe({
      next: (res) => {
        if (res && res.result) {
          const fathers = res.result.filter((p: any) => p.relation === ParentsRelation.Father);
          const mothers = res.result.filter((p: any) => p.relation === ParentsRelation.Mother);

          let latestUpdate: any = null;
          let latestCreate: any = null;

          if (fathers.length > 0) {
            const f = fathers[0];
            this.fatherId = f.id;
            this.parentDetailsForm.patchValue({
              fatherFullName: f.name,
              fatherEmail: f.email,
              fatherPhone: f.phone
            });
            if (f.updatedOn) latestUpdate = f.updatedOn;
            if (f.createdOn) latestCreate = f.createdOn;
          }
          if (mothers.length > 0) {
            const m = mothers[0];
            this.motherId = m.id;
            this.parentDetailsForm.patchValue({
              motherFullName: m.name,
              motherEmail: m.email,
              motherPhone: m.phone
            });
            if (m.updatedOn && (!latestUpdate || new Date(m.updatedOn) > new Date(latestUpdate))) latestUpdate = m.updatedOn;
            if (m.createdOn && (!latestCreate || new Date(m.createdOn) > new Date(latestCreate))) latestCreate = m.createdOn;
          }
          this.setLastUpdatedText(latestUpdate, latestCreate);
        }
      }
    });
  }


  setLastUpdatedText(updatedOn?: Date | string | null, createdOn?: Date | string | null) {
    if (updatedOn) {
      const date = new Date(updatedOn);
      this.lastUpdatedText = `Updated on ${date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}`;
    } else if (createdOn) {
      const date = new Date(createdOn);
      this.lastUpdatedText = `Added on ${date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}`;
    } else {
      this.lastUpdatedText = 'Last updated 3 months ago';
    }
  }

  mobileValidator(control: any) {
    if (!control.value) return null;
    const isNum = /^\d+$/.test(control.value);
    if (!isNum) {
      return { nonDigit: true };
    }
    if (control.value.length !== 10) {
      return { lengthError: true };
    }
    return null;
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.parentDetailsForm.invalid) {
      return;
    }
    if (!this.parentDetailsForm.dirty) {
      this.messageService.add({ severity: 'info', summary: 'Info', detail: 'No changes detected.' });
      return;
    }
    if (!this.studentId) return;

    if (this.loading) return;
    this.loading = true;

    const values = this.parentDetailsForm.value;
    const requests: Observable<any>[] = [];

    // Father request
    const fatherReq: ParentsInformationRequest = {
      studentId: this.studentId,
      name: values.fatherFullName || null,
      email: values.fatherEmail || null,
      phone: values.fatherPhone || null,
      relation: ParentsRelation.Father
    };
    if (this.fatherId) {
      requests.push(this.parentsInfoService.updateParentsInformation(this.fatherId, fatherReq));
    } else {
      requests.push(this.parentsInfoService.createParentsInformation(fatherReq));
    }

    // Mother request
    if (values.motherFullName || values.motherEmail || values.motherPhone) {
      const motherReq: ParentsInformationRequest = {
        studentId: this.studentId,
        name: values.motherFullName || null,
        email: values.motherEmail || null,
        phone: values.motherPhone || null,
        relation: ParentsRelation.Mother
      };
      if (this.motherId) {
        requests.push(this.parentsInfoService.updateParentsInformation(this.motherId, motherReq));
      } else {
        requests.push(this.parentsInfoService.createParentsInformation(motherReq));
      }
    }

    forkJoin(requests).subscribe({
      next: (responses) => {
        this.loading = false;
        this.parentDetailsForm.markAsPristine();
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Parent details updated.' });
        this.setLastUpdatedText(new Date(), null);
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Failed to update parent details.' });
      }
    });
  }
}
